import { useRef } from "react";
import { toast } from "sonner";
import { Camera, ChevronRight, ChevronLeft } from "lucide-react";
import { ProductImage } from "./Bits";
import { api, errMsg } from "../lib/api";

const CategoryTile = ({ c, i, isAdmin, onOpen, onChanged, reorder, move, last }) => {
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/categories/image", fd, { params: { name: c.key } });
      toast.success(`${c.name} fotoğrafı güncellendi`);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`category-tile-${c.name}`} style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
      className="fade-up group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg">
      <button type="button" data-testid={`category-open-${c.name}`} onClick={() => !reorder && onOpen(c.key)} className="block w-full text-left">
        <div className="relative aspect-[4/3] overflow-hidden">
          <ProductImage product={{ ...c, name: c.name }} className="h-full w-full transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4 text-white">
            <div>
              <h3 className="font-display text-xl font-bold leading-tight">{c.name}</h3>
              <p className="font-mono text-[11px] uppercase tracking-wider text-cyan-200" data-testid={`category-count-${c.name}`}>{c.count} ürün</p>
            </div>
            <span className="grid h-9 w-9 place-items-center rounded-full bg-white/15 backdrop-blur transition-colors group-hover:bg-blue-600"><ChevronRight className="h-5 w-5" /></span>
          </div>
        </div>
      </button>
      {reorder && (
        <div className="absolute inset-x-0 top-0 flex justify-between p-3" data-testid={`category-reorder-${c.name}`}>
          <button type="button" disabled={i === 0} data-testid={`category-move-left-${c.name}`} onClick={() => move(i, -1)} className="grid h-10 w-10 place-items-center rounded-full bg-blue-600 text-white shadow disabled:opacity-30"><ChevronLeft className="h-5 w-5" /></button>
          <span className="rounded-full bg-white/90 px-3 py-2 font-mono text-xs font-bold">{i + 1}</span>
          <button type="button" disabled={last} data-testid={`category-move-right-${c.name}`} onClick={() => move(i, 1)} className="grid h-10 w-10 place-items-center rounded-full bg-blue-600 text-white shadow disabled:opacity-30"><ChevronRight className="h-5 w-5" /></button>
        </div>
      )}
      {isAdmin && !reorder && c.key !== "" && <>
        <button type="button" data-testid={`category-photo-button-${c.name}`} onClick={() => ref.current?.click()} title="Kategori fotoğrafı ekle"
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-slate-700 shadow transition-colors hover:bg-blue-600 hover:text-white">
          <Camera className="h-4 w-4" />
        </button>
        <input ref={ref} data-testid={`category-photo-input-${c.name}`} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      </>}
    </div>
  );
};

export const CategoryGrid = ({ categories, isAdmin, onOpen, onChanged, reorder, setCategories }) => {
  const move = async (i, d) => {
    const next = [...categories];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setCategories(next);
    try { await api.put("/categories/order", { keys: next.map((c) => c.key) }); } catch (e) { toast.error(errMsg(e)); onChanged(); }
  };
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="category-grid">
      {categories.map((c, i) => <CategoryTile key={c.key || "_none"} c={c} i={i} isAdmin={isAdmin} onOpen={onOpen} onChanged={onChanged}
        reorder={reorder} move={move} last={i === categories.length - 1} />)}
    </div>
  );
};
